import { type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router";
import { ExportDataModal } from "~/molecules/ExportDataModal";
import { DownloadDatabaseModal } from "~/organisms/library/DownloadDatabaseModal";
import HeadingPage from "~/molecules/HeadingPage";
import { PlatformRole } from "~/config/types";
import { useUserContext } from "~/hooks/useUserContext";
import { useTranslation } from "react-i18next";
import { NewDocumentButton } from "~/molecules/NewDocumentButton";
import { Button } from "~/atoms/button";
import { TagsModal } from "~/organisms/TagsModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "~/atoms/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { DocumentVersionsList } from "~/organisms/library/DocumentVersionsList";
import { useDocumentVersionsListModel } from "~/hooks/useDocumentVersionsListModel";
import {
  buildSearchParamValuesFromFilters,
  buildSearchParams,
  FilterOperatorEnum,
} from "~/helpers/documentFilters";

export const Library = () => {
  const [searchParams] = useSearchParams();
  const { user } = useUserContext();
  const { t } = useTranslation();
  const dvListModel = useDocumentVersionsListModel();
  const defaultDocumentsSearch = buildSearchParams({
    ...buildSearchParamValuesFromFilters([
      {
        field: "status",
        operator: FilterOperatorEnum.eq,
        value: "published",
      },
    ]),
    page: "1",
  }).toString();

  const isReader = user.role === PlatformRole.reader;

  if (searchParams.toString().length === 0) {
    return <Navigate to={`/documents?${defaultDocumentsSearch}`} replace />;
  }

  const renderActionMenuItem = (label: ReactNode) => (
    <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
      {label}
    </DropdownMenuItem>
  );

  return (
    <div className="space-y-4 px-6 py-7">
      <div className="flex justify-between">
        <div>
          <HeadingPage title={t(($) => $.library.header)} />
          <p className="text-muted-foreground">
            {t(($) => $.commons.workflow.browseDescription)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isReader && (
            <>
              <NewDocumentButton />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DownloadDatabaseModal
                    trigger={renderActionMenuItem(
                      t(($) => $.commons.download.documents),
                    )}
                    documentVersions={dvListModel.filteredDocumentVersions}
                  />
                  <ExportDataModal
                    trigger={renderActionMenuItem(
                      t(($) => $.commons.workflow.downloadReport),
                    )}
                    documentVersions={dvListModel.filteredDocumentVersions}
                  />
                  <TagsModal
                    trigger={renderActionMenuItem(
                      t(($) => $.modal.tags.manageTags),
                    )}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      <DocumentVersionsList model={dvListModel} />
    </div>
  );
};
